import { useCallback, useEffect, useState } from 'react';
import type {
  ApolloError,
  ApolloQueryResult,
  QueryResult,
} from '@apollo/client';
import type { PageInfo } from '../generated';

export type CursorPaginationPageInfo = Pick<
  PageInfo,
  'endCursor' | 'hasNextPage'
>;

export type CursorPaginationBackwardsPageInfo = Pick<
  PageInfo,
  'startCursor' | 'hasPreviousPage'
>;

export type FetchNextInput = {
  count: number;
  pageInfo: CursorPaginationPageInfo;
};

export type FetchPreviousInput = {
  count: number;
  pageInfo: CursorPaginationBackwardsPageInfo;
};

export const useCursorPagination = <T extends QueryResult<any, any>>(
  queryHookResult: T,
  stopOnError: boolean = false
) => {
  const queryVariables = queryHookResult?.variables;
  const fetchMore = queryHookResult?.fetchMore;
  const [fetchError, setFetchError] = useState<ApolloError | undefined>(
    queryHookResult.error
  );

  const fetchMoreParams = useCallback(
    (
      { count, pageInfo }: FetchNextInput,
      newVariables: Partial<T['variables']> = {}
    ) => {
      const endCursor = pageInfo?.endCursor;
      return {
        variables: {
          ...queryVariables,
          ...newVariables,
          page: {
            first: count,
            after: endCursor,
          },
        },
      };
    },
    [queryVariables]
  );

  const fetchPreviousParams = useCallback(
    (
      { count, pageInfo }: FetchPreviousInput,
      newVariables: Partial<T['variables']> = {}
    ) => {
      const startCursor = pageInfo?.startCursor;
      return {
        variables: {
          ...queryVariables,
          ...newVariables,
          page: {
            last: count,
            before: startCursor,
          },
        },
      };
    },
    [queryVariables]
  );

  useEffect(() => {
    if (queryHookResult.error && !fetchError) {
      setFetchError(queryHookResult.error);
    }
  }, [queryHookResult.error, fetchError]);

  const fetchNext = useCallback(
    (
      input: FetchNextInput,
      newVariables?: Partial<T['variables']>
    ): Promise<ApolloQueryResult<T['data']>> | null => {
      if (fetchError && stopOnError) {
        return null;
      }
      const hasNextPage = input?.pageInfo?.hasNextPage;
      if (!hasNextPage) {
        return null;
      }
      const result = fetchMore(fetchMoreParams(input, newVariables));
      result.catch(setFetchError);
      return result;
    },
    [fetchMore, fetchMoreParams, setFetchError, fetchError, stopOnError]
  );

  const fetchPrevious = useCallback(
    (
      input: FetchPreviousInput,
      newVariables?: Partial<T['variables']>
    ): Promise<ApolloQueryResult<T['data']>> | null => {
      if (fetchError && stopOnError) {
        return null;
      }
      const hasPreviousPage = input?.pageInfo?.hasPreviousPage;
      if (!hasPreviousPage) {
        return null;
      }
      const result = fetchMore(fetchPreviousParams(input, newVariables));
      result.catch(setFetchError);
      return result;
    },
    [fetchMore, fetchPreviousParams, setFetchError, fetchError, stopOnError]
  );

  return {
    ...queryHookResult,
    fetchNext,
    fetchPrevious,
  };
};
